import { EventEmitter } from "../events/eventEmitter";
import { log } from "../log";
import { GuideScheduleReason } from "../scheduler/scheduler";
import { SessionStateStorage } from "./persistent-storage/sessionState";
import { TourState, TourStateStorage } from "./persistent-storage/tourState";
import { TourTerminateReason } from "./tourLifecycleManager";
import { TourSessionManagerEvents, TourSessionManagerNamespace } from "./tourSessionManager.events";

export interface TourSessionManagerParams {
  tourStateStorage: TourStateStorage;
  sessionStateStorage: SessionStateStorage;
}

export type TourSession = {
  sessionId: string;
  startedAt: number;
};

export class TourSessionManager {
  private tourStateStorage: TourStateStorage;
  private sessionStateStorage: SessionStateStorage;
  static readonly defaultTourExpirationDuration = 10000;
  private expirationCheckTimer: NodeJS.Timeout | undefined;
  events = new EventEmitter<typeof TourSessionManagerNamespace, TourSessionManagerEvents>(TourSessionManagerNamespace);
  activeSessionId?: string;

  constructor({ tourStateStorage, sessionStateStorage }: TourSessionManagerParams) {
    this.tourStateStorage = tourStateStorage;
    this.sessionStateStorage = sessionStateStorage;
    this.activeSessionId = this.tourStateStorage.get().sessionId;
    this.clearTourSessionIfExpired();
  }

  initializeTourSession(reason: GuideScheduleReason): TourSession {
    const startedAt = this.tourStateStorage.initStartedAt();
    const sessionId = this.tourStateStorage.initSessionId();
    this.activeSessionId = sessionId;
    if (reason === "tour_session_recovered") {
      this.events.emit("tourSessionResumed", { sessionId });
    } else {
      this.events.emit("tourSessionStarted", { sessionId, startedAt, reason });
    }

    this.startExpirationTimer();

    return {
      startedAt,
      sessionId,
    };
  }

  terminateTourSession(reason: TourTerminateReason) {
    if (!this.activeSessionId) {
      return;
    }

    this.events.emit("tourSessionTerminated", { reason, sessionId: this.activeSessionId });
    this.stopExpirationTimer();
    this.activeSessionId = undefined;
    this.tourStateStorage.clear();
  }

  getRecoverableTourSession(): TourState | undefined {
    const tourState = this.tourStateStorage.get();
    if (!tourState.activeTourGuideId) {
      return undefined;
    }

    if (this.isTourSessionExpired(tourState)) {
      return undefined;
    }

    return tourState;
  }

  isTourSessionExpired(tourState: TourState): boolean {
    const lastStepTransitionTimestamp = tourState.lastStepTransitionTimestamp;
    const sessionStartedAt = tourState.startedAt;

    let tZero: number | undefined;

    if (lastStepTransitionTimestamp) {
      tZero = lastStepTransitionTimestamp;
    }

    if (!tZero && sessionStartedAt) {
      tZero = sessionStartedAt;
    }

    if (!tZero) {
      return true;
    }

    const expirationTimeoutDuration = tourState.expirationTimeoutMinutes
      ? tourState.expirationTimeoutMinutes * 60 * 1000
      : TourSessionManager.defaultTourExpirationDuration;

    const timePassed = Date.now() - tZero;
    return timePassed > expirationTimeoutDuration;
  }

  private clearTourSessionIfExpired(): boolean {
    const tourState = this.tourStateStorage.get();
    if (!tourState.activeTourGuideId) {
      return false;
    }

    if (this.isTourSessionExpired(tourState)) {
      this.terminateTourSession("session_expired");
      this.sessionStateStorage.addExpiredGuide(tourState.activeTourGuideId);
      this.events.emit("tourSessionExpired", { sessionId: tourState.sessionId! });
      return true;
    }

    return false;
  }

  private startExpirationTimer() {
    if (this.expirationCheckTimer) {
      clearInterval(this.expirationCheckTimer);
    }

    // Check every 10 seconds
    this.expirationCheckTimer = setInterval(() => {
      const tourState = this.tourStateStorage.get();
      if (this.isTourSessionExpired(tourState)) {
        log("Tour session has expired");

        this.sessionStateStorage.addExpiredGuide(tourState.activeTourGuideId!);
        this.events.emit("tourSessionExpired", { sessionId: tourState.sessionId! });
        this.stopExpirationTimer();
      }
    }, 10000);
  }

  private stopExpirationTimer() {
    if (this.expirationCheckTimer) {
      window.clearInterval(this.expirationCheckTimer);
      this.expirationCheckTimer = undefined;
    }
  }
}
